import type {
  IPersonAutoMatchMultiResult,
  IPersonAutoMatchResult,
} from '@skystone/shared';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';

interface AutoMatchStatusCardProps {
  lastRunAt: string | null;
  lastResult: IPersonAutoMatchResult | null;
  bySource?: IPersonAutoMatchMultiResult | null;
  isRunning: boolean;
  onRun: () => void;
}

function formatRunAt(iso: string | null): string {
  if (iso === null) return 'Never';
  const date = new Date(iso);
  return date.toLocaleString();
}

function ResultList({
  label,
  result,
}: Readonly<{ label: string; result: IPersonAutoMatchResult }>) {
  return (
    <div>
      <p className="font-medium">{label}</p>
      <ul className="mt-1 space-y-0.5 text-muted-foreground">
        <li>Linked: {result.linked}</li>
        <li>Skipped (no email): {result.skippedNoEmail}</li>
        <li>Skipped (bot): {result.skippedBot}</li>
        <li>Skipped (already linked): {result.skippedAlreadyLinked}</li>
        <li>Ambiguous (multiple Persons share the email): {result.ambiguous}</li>
      </ul>
    </div>
  );
}

export function AutoMatchStatusCard({
  lastRunAt,
  lastResult,
  bySource,
  isRunning,
  onRun,
}: Readonly<AutoMatchStatusCardProps>) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Auto-match</CardTitle>
        <CardDescription>
          Links Entra, Slack, and GitHub accounts to canonical Persons by email. Runs after each
          source sync; trigger manually here if needed.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="text-sm text-muted-foreground">
            Last run: <span className="font-medium text-foreground">{formatRunAt(lastRunAt)}</span>
          </div>
          <Button onClick={onRun} isLoading={isRunning} disabled={isRunning}>
            Run auto-match now
          </Button>
        </div>

        {lastResult && (
          <div className="mt-4 space-y-3 rounded-md border bg-muted/40 p-3 text-sm">
            <ResultList label="Last run summary (total)" result={lastResult} />
            {bySource && (
              <div className="grid gap-3 border-t pt-3 sm:grid-cols-3">
                {/* Defensive: an older auto-match result that predates the
                    Entra source kind won't have a bySource.entra field, so
                    guard the render to avoid crashing on legacy payloads. */}
                {bySource.entra && <ResultList label="Entra" result={bySource.entra} />}
                <ResultList label="Slack" result={bySource.slack} />
                <ResultList label="GitHub" result={bySource.github} />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
